/**
 * Shared geometry and SVG helpers for dynamic trade-flow routes.
 */
import {
	DEFAULT_FLOWS,
	DEFAULT_LOCATIONS,
	MAP_SVG_LEGACY_VIEWBOX,
	getDefaultLabelOffset,
	parseLabelOffset,
} from './constants';

const SVG_NS = 'http://www.w3.org/2000/svg';
const ROUTE_DELAY_STEP = 0.42;

/**
 * Convert legacy marker percentages into SVG artboard coordinates.
 *
 * @param {number} xPercent Horizontal position (0–100).
 * @param {number} yPercent Vertical position (0–100).
 * @return {{ x: number, y: number }|null} SVG point or null when invalid.
 */
export function markerPercentToSvg( xPercent, yPercent ) {
	const x = Number( xPercent );
	const y = Number( yPercent );

	if ( ! Number.isFinite( x ) || ! Number.isFinite( y ) ) {
		return null;
	}

	return {
		x: ( x / 100 ) * MAP_SVG_LEGACY_VIEWBOX.width,
		y: ( y / 100 ) * MAP_SVG_LEGACY_VIEWBOX.height,
	};
}

/**
 * Build a quadratic Bézier that lifts north of the midpoint.
 *
 * @param {{ x: number, y: number }} origin      Start point in SVG units.
 * @param {{ x: number, y: number }} destination End point in SVG units.
 * @return {string} SVG path `d` value.
 */
export function createTradeFlowPath( origin, destination ) {
	const x1 = origin.x;
	const y1 = origin.y;
	const x2 = destination.x;
	const y2 = destination.y;
	const dist = Math.hypot( x2 - x1, y2 - y1 ) || 1;
	const midX = ( x1 + x2 ) / 2;
	const midY = ( y1 + y2 ) / 2;
	const lift = Math.min( 120, Math.max( 36, dist * 0.18 ) );

	return `M${ x1.toFixed( 1 ) } ${ y1.toFixed( 1 ) } Q${ midX.toFixed(
		1
	) } ${ ( midY - lift ).toFixed( 1 ) } ${ x2.toFixed( 1 ) } ${ y2.toFixed(
		1
	) }`;
}

/**
 * Look up a location by id.
 *
 * @param {Array}  locations Location records.
 * @param {string} id        Location id.
 * @return {Object|null} Location or null.
 */
export function getLocationById( locations, id ) {
	if ( ! id || ! Array.isArray( locations ) ) {
		return null;
	}

	return locations.find( ( location ) => location?.id === id ) || null;
}

/**
 * Whether a flow can be drawn.
 *
 * @param {Object} flow      Flow record.
 * @param {Array}  locations Location records.
 * @return {boolean} True when origin and destination are valid and distinct.
 */
export function isRenderableFlow( flow, locations ) {
	if ( ! flow || flow.enabled === false ) {
		return false;
	}

	const from = getLocationById( locations, flow.fromLocationId );
	const to = getLocationById( locations, flow.toLocationId );
	const fromPoint = from ? markerPercentToSvg( from.x, from.y ) : null;
	const toPoint = to ? markerPercentToSvg( to.x, to.y ) : null;

	if ( ! fromPoint || ! toPoint ) {
		return false;
	}

	if ( from.id === to.id ) {
		return false;
	}

	return true;
}

/**
 * Remove previously generated (or hardcoded) route groups from an SVG.
 *
 * @param {SVGElement|null} svg Root SVG element.
 */
export function clearTradeFlowRoutes( svg ) {
	if ( ! svg || typeof svg.querySelectorAll !== 'function' ) {
		return;
	}

	svg.querySelectorAll( '.tf-route-set' ).forEach( ( group ) => {
		group.remove();
	} );
}

/**
 * Draw animated trade-flow paths for the given data.
 *
 * Invalid flows are skipped. Existing `.tf-route-set` groups are replaced.
 *
 * @param {SVGElement|null} svg             Root SVG element.
 * @param {Object}          root0           Data.
 * @param {Array}           root0.flows     Trade flows.
 * @param {Array}           root0.locations Locations.
 * @param {string}          activeCategory  Currently visible category id.
 */
export function renderTradeFlowsIntoSvg(
	svg,
	{ flows = [], locations = [] } = {},
	activeCategory = ''
) {
	if ( ! svg || typeof svg.appendChild !== 'function' ) {
		return;
	}

	clearTradeFlowRoutes( svg );

	const doc = svg.ownerDocument || document;
	const safeFlows = Array.isArray( flows ) ? flows : [];
	const safeLocations = Array.isArray( locations ) ? locations : [];
	const byCategory = {};

	safeFlows.forEach( ( flow ) => {
		if ( ! isRenderableFlow( flow, safeLocations ) ) {
			return;
		}

		const categoryId = flow.category || 'minerals-metals';

		if ( ! byCategory[ categoryId ] ) {
			byCategory[ categoryId ] = [];
		}

		byCategory[ categoryId ].push( flow );
	} );

	Object.keys( byCategory ).forEach( ( categoryId ) => {
		const set = doc.createElementNS( SVG_NS, 'g' );
		set.setAttribute( 'class', 'tf-route-set' );
		set.setAttribute( 'data-category', categoryId );

		if ( categoryId === activeCategory ) {
			set.classList.add( 'is-active' );
		}

		byCategory[ categoryId ].forEach( ( flow, index ) => {
			const from = getLocationById( safeLocations, flow.fromLocationId );
			const to = getLocationById( safeLocations, flow.toLocationId );
			const origin = markerPercentToSvg( from.x, from.y );
			const destination = markerPercentToSvg( to.x, to.y );
			const corridor = doc.createElementNS( SVG_NS, 'g' );
			const delay = Number.isFinite( Number( flow.delay ) )
				? Number( flow.delay )
				: index * ROUTE_DELAY_STEP;

			corridor.setAttribute( 'class', 'tf-corridor' );
			corridor.setAttribute(
				'style',
				`--route-delay:${ delay.toFixed( 2 ) }s`
			);
			corridor.setAttribute( 'data-flow-id', flow.id || '' );

			const path = doc.createElementNS( SVG_NS, 'path' );
			path.setAttribute( 'class', 'tf-route' );
			path.setAttribute( 'pathLength', '1' );
			path.setAttribute(
				'd',
				createTradeFlowPath( origin, destination )
			);

			corridor.appendChild( path );
			set.appendChild( corridor );
		} );

		svg.appendChild( set );
	} );
}

/**
 * Toggle the active route set to match the selected category.
 *
 * @param {HTMLElement|null} mapEl Map root.
 */
export function syncActiveTradeFlowRoutes( mapEl ) {
	if ( ! mapEl ) {
		return;
	}

	const category = mapEl.getAttribute( 'data-active-category' ) || '';

	mapEl.querySelectorAll( '.tf-route-set' ).forEach( ( group ) => {
		group.classList.toggle(
			'is-active',
			group.getAttribute( 'data-category' ) === category
		);
	} );

	mapEl
		.querySelectorAll( '.alteia-trade-flow-map-section__marker' )
		.forEach( ( marker ) => {
			marker.classList.toggle(
				'is-category-visible',
				marker.getAttribute( 'data-marker-category' ) === category
			);
		} );
}

/**
 * Parse locations/flows JSON from map data attributes.
 *
 * @param {HTMLElement|null} mapEl Map root.
 * @return {{ locations: Array, flows: Array }} Parsed data.
 */
export function parseMapDataset( mapEl ) {
	const parse = ( value ) => {
		if ( ! value ) {
			return [];
		}

		try {
			const parsed = JSON.parse( value );
			return Array.isArray( parsed ) ? parsed : [];
		} catch ( error ) {
			return [];
		}
	};

	const rawLocations = mapEl
		? mapEl.getAttribute( 'data-trade-locations' )
		: null;
	const rawFlows = mapEl ? mapEl.getAttribute( 'data-trade-flows' ) : null;
	const parsedLocations =
		rawLocations === null || rawLocations === ''
			? null
			: parse( rawLocations );
	const parsedFlows =
		rawFlows === null || rawFlows === '' ? null : parse( rawFlows );

	const defaultLocations = Array.isArray( DEFAULT_LOCATIONS )
		? DEFAULT_LOCATIONS
		: [];
	const defaultFlows = Array.isArray( DEFAULT_FLOWS ) ? DEFAULT_FLOWS : [];

	return {
		locations:
			parsedLocations !== null ? parsedLocations : defaultLocations,
		flows: parsedFlows !== null ? parsedFlows : defaultFlows,
	};
}

/**
 * Apply per-location label offsets on rendered markers (name only).
 *
 * @param {HTMLElement|null} mapEl Map root.
 */
export function applyMarkerLabelOffsets( mapEl ) {
	if ( ! mapEl ) {
		return;
	}

	const { locations } = parseMapDataset( mapEl );
	const byId = {};
	const byLabel = {};

	( Array.isArray( locations ) ? locations : [] ).forEach( ( location ) => {
		if ( location?.id ) {
			byId[ location.id ] = location;
		}

		if ( location?.label ) {
			byLabel[ location.label ] = location;
		}
	} );

	mapEl
		.querySelectorAll( '.alteia-trade-flow-map-section__marker' )
		.forEach( ( marker ) => {
			const locationId = marker.getAttribute( 'data-location-id' ) || '';
			const labelEl = marker.querySelector(
				'.alteia-trade-flow-map-section__marker-label'
			);
			const label = labelEl ? labelEl.textContent.trim() : '';
			const location =
				( locationId && byId[ locationId ] ) ||
				( label && byLabel[ label ] ) ||
				null;
			const seed = getDefaultLabelOffset(
				location?.id || locationId,
				location?.label || label
			);

			// Prefer offsets from data-trade-locations. If older saved markup
			// omitted them, keep MapMarker CSS vars so editor-saved positions
			// are not overwritten by seeded defaults.
			const offsetX = resolveMarkerLabelOffset(
				location,
				'labelOffsetX',
				marker,
				'--alteia-trade-flow-map-section-label-offset-x',
				seed.x
			);
			const offsetY = resolveMarkerLabelOffset(
				location,
				'labelOffsetY',
				marker,
				'--alteia-trade-flow-map-section-label-offset-y',
				seed.y
			);

			marker.style.setProperty(
				'--alteia-trade-flow-map-section-label-offset-x',
				String( offsetX )
			);
			marker.style.setProperty(
				'--alteia-trade-flow-map-section-label-offset-y',
				String( offsetY )
			);
		} );
}

/**
 * Resolve one label offset from location data, existing CSS, or a seed.
 *
 * @param {Object|null} location Location record from the map dataset.
 * @param {string}      key      Location offset key (labelOffsetX|labelOffsetY).
 * @param {HTMLElement} marker   Marker element.
 * @param {string}      cssVar   CSS custom property name on the marker.
 * @param {number}      seed     Default offset when nothing else is available.
 * @return {number} Resolved offset.
 */
function resolveMarkerLabelOffset( location, key, marker, cssVar, seed ) {
	if (
		location &&
		Object.prototype.hasOwnProperty.call( location, key )
	) {
		return parseLabelOffset( location[ key ], seed );
	}

	const existing = marker.style.getPropertyValue( cssVar ).trim();

	if ( existing !== '' ) {
		const parsed = Number( existing );
		if ( Number.isFinite( parsed ) ) {
			return parsed;
		}
	}

	return seed;
}
